/**
 * Wine regions per country (owner requests, 2026-10-03): the best-known wine regions of every
 * country of the app, in their own spelling as they appear on labels. A wine's region is
 * mandatory and must be one of its country's, or `REGION_OTHER` ("Autre région") when it is not
 * listed. Stored as the region's name.
 */

/** "Autre région": accepted for any country, so an unlisted region never blocks a purchase. */
export const REGION_OTHER = 'other';

export const REGIONS: Readonly<Record<string, readonly string[]>> = {
  FR: [
    'Alsace',
    'Beaujolais',
    'Bordeaux',
    'Bourgogne',
    'Champagne',
    'Corse',
    'Jura',
    'Languedoc',
    'Loire',
    'Provence',
    'Roussillon',
    'Savoie',
    'Sud-Ouest',
    'Vallée du Rhône',
  ],
  IT: [
    'Abruzzo',
    'Alto Adige',
    'Campania',
    'Emilia-Romagna',
    'Friuli-Venezia Giulia',
    'Lombardia',
    'Marche',
    'Piemonte',
    'Puglia',
    'Sardegna',
    'Sicilia',
    'Toscana',
    'Trentino',
    'Umbria',
    'Veneto',
  ],
  ES: [
    'Bierzo',
    'Cava',
    'Jerez',
    'Jumilla',
    'La Mancha',
    'Navarra',
    'Penedès',
    'Priorat',
    'Rías Baixas',
    'Ribera del Duero',
    'Rioja',
    'Rueda',
    'Toro',
  ],
  PT: [
    'Alentejo',
    'Bairrada',
    'Dão',
    'Douro',
    'Lisboa',
    'Madeira',
    'Porto',
    'Setúbal',
    'Vinho Verde',
  ],
  DE: [
    'Ahr',
    'Baden',
    'Franken',
    'Mosel',
    'Nahe',
    'Pfalz',
    'Rheingau',
    'Rheinhessen',
    'Württemberg',
  ],
  AT: ['Burgenland', 'Kamptal', 'Kremstal', 'Steiermark', 'Wachau', 'Weinviertel', 'Wien'],
  CH: ['Genève', 'Neuchâtel', 'Tessin', 'Valais', 'Vaud'],
  US: [
    'Central Coast',
    'Columbia Valley',
    'Finger Lakes',
    'Lodi',
    'Mendocino',
    'Napa Valley',
    'Paso Robles',
    'Santa Barbara',
    'Sonoma',
    'Willamette Valley',
  ],
  AR: ['La Rioja', 'Mendoza', 'Patagonia', 'Salta', 'San Juan'],
  CL: ['Aconcagua', 'Bío-Bío', 'Casablanca', 'Colchagua', 'Itata', 'Maipo', 'Maule', 'Rapel'],
  AU: [
    'Adelaide Hills',
    'Barossa Valley',
    'Clare Valley',
    'Coonawarra',
    'Eden Valley',
    'Hunter Valley',
    'Margaret River',
    'McLaren Vale',
    'Tasmania',
    'Yarra Valley',
  ],
  NZ: [
    'Canterbury',
    'Central Otago',
    'Gisborne',
    "Hawke's Bay",
    'Marlborough',
    'Martinborough',
    'Nelson',
  ],
  ZA: ['Constantia', 'Elgin', 'Franschhoek', 'Paarl', 'Stellenbosch', 'Swartland', 'Walker Bay'],
  GR: ['Crète', 'Macédoine', 'Naoussa', 'Némée', 'Santorin'],
  HU: ['Eger', 'Tokaj', 'Villány'],
  GB: ['Cornwall', 'East Sussex', 'Essex', 'Hampshire', 'Kent', 'West Sussex'],
  CA: [
    'British Columbia',
    'Niagara Peninsula',
    'Nova Scotia',
    'Okanagan Valley',
    'Prince Edward County',
  ],
  GE: ['Imereti', 'Kakheti', 'Kartli', 'Racha-Lechkhumi'],
  MA: ['Benslimane', 'Guerrouane', 'Meknès', 'Zenata'],
  LB: ['Batroun', 'Mont-Liban', 'Vallée de la Bekaa'],
  IL: ['Galilée', 'Golan', 'Judée', 'Samson', 'Shomron'],
  HR: ['Dalmatie', 'Istrie', 'Slavonie'],
  SI: ['Goriška Brda', 'Podravje', 'Posavje', 'Primorska', 'Vipava'],
  RO: ['Banat', 'Crişana', 'Dealu Mare', 'Moldova', 'Murfatlar', 'Transilvania'],
  BG: ['Danube Plain', 'Black Sea', 'Struma Valley', 'Thracian Valley'],
  MD: ['Codru', 'Ştefan Vodă', 'Valul lui Traian'],
  JP: ['Hokkaido', 'Nagano', 'Yamagata', 'Yamanashi'],
  CN: ['Hebei', 'Ningxia', 'Shandong', 'Xinjiang', 'Yunnan'],
  UY: ['Canelones', 'Colonia', 'Maldonado', 'Montevideo'],
  BR: ['Campanha', 'Serra Gaúcha', 'Serra do Sudeste', 'Vale do São Francisco'],
  MX: ['Coahuila', 'Querétaro', 'Valle de Guadalupe', 'Zacatecas'],
  LU: ['Moselle luxembourgeoise'],
  BE: ['Côtes de Sambre et Meuse', 'Hageland', 'Haspengouw', 'Heuvelland'],
  NL: ['Gelderland', 'Limburg', 'Zeeland'],
};

/** A country's regions in alphabetical order (French order, ignoring accents); none if unknown. */
export function regionsFor(countryCode: string): string[] {
  return [...(REGIONS[countryCode] ?? [])].sort((a, b) =>
    a.localeCompare(b, 'fr', { sensitivity: 'base' }),
  );
}

/** The region as shown and exported: its name, or "Autre région" / "Other region". */
export function regionName(region: string, other: string): string {
  return region === REGION_OTHER ? other : region;
}

/** Whether `region` is one of the country's, or "other" for a known country. */
export function isRegionOf(countryCode: string, region: string): boolean {
  const regions = REGIONS[countryCode];
  return regions !== undefined && (region === REGION_OTHER || regions.includes(region));
}
